import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { MatDialog } from '@angular/material/dialog';
import { TipoCustoKitFabricadoEnum } from '../../../../../shared/enums/produto/tipo-custo-kit-fabricado.enum';
import { ConfiguracaoProduto } from '../../../../../shared/services/models/produto/configuracao-produto.model';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-produto-producao',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatSlideToggleModule,
        SelectPesquisavelComponent,
        NgxCurrencyDirective,
        AbntPipe,
        EmptyStateComponent,
        DragDropModule
    ],
    templateUrl: './produto-producao.component.html',
    styleUrl: './produto-producao.component.css'
})
export class ProdutoProducaoComponent {
    @Input({ required: true }) form!: FormGroup;
    @Input({ required: true }) isFabricado!: boolean;
    @Input({ required: true }) urlProduto!: string;
    @Input({ required: true }) configuracaoProduto!: ConfiguracaoProduto;
    @Input({ required: true }) isEditMode!: boolean;
    @Input() quantidadeMask!: any;
    @Input() set dadosEstruturaProducaoCarregados(dados: any) {
        if (dados) this.preencherInsumos(dados);
    }
    @Input() set dadosEtapasProducaoCarregados(dados: any) {
        if (dados) this.preencherEtapas(dados);
    }

    // Evento para atualizar custo no pai (similar a produto-kits)
    @Output() custoAtualizado = new EventEmitter<number>();

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog
    ) { }

    get producao(): FormArray {
        return this.form.get('producao') as FormArray;
    }

    get etapasProducao(): FormArray {
        return this.form.get('etapasProducao') as FormArray;
    }

    getControl(item: any, controlName: string): FormControl {
        return item.get(controlName) as FormControl;
    }

    addProducaoRow(data?: any): void {
        const produtoList: ProdutoListStruct = data?.produtoListStruct;

        const group = this.fb.group({
            idProdutoInsumo: [data?.idProdutoInsumo, Validators.required],
            quantidade: [data?.quantidade || 1],
            idUnidadeConsumo: [data?.idUnidadeConsumo || null],
            // Identifier for existing items
            idProdutoEstruturaProducao: [data?.idProdutoEstruturaProducao || null],
            isExistingItem: [!!data?.idProdutoEstruturaProducao], // Flag to identify if item already exists
            // UI fields
            nomeProdutoInsumo: [{ value: data?.nomeProdutoInsumo || produtoList?.descricao || '', disabled: true }],
            skuProdutoInsumo: [{ value: data?.skuProdutoInsumo || produtoList?.codigoSku || '', disabled: true }],
            unidade: [{ value: data?.unidade || produtoList?.unidade || '', disabled: true }],
            precoCusto: [{ value: data?.precoCusto || produtoList?.custo || 0, disabled: true }],
            custoMedio: [{ value: data?.custoMedio || produtoList?.custoMedio || 0, disabled: true }],
            custoExibido: [{ value: data?.custoExibido || this.getCustoExibido(produtoList?.custo || 0, produtoList?.custoMedio || 0) || 0, disabled: true }],
            total: [{ value: (data?.quantidade || 1) * (data?.custoExibido || this.getCustoExibido(produtoList?.custo || 0, produtoList?.custoMedio || 0) || 0), disabled: true }],
            // Helper for Select Component Preload
            preloadedList: [produtoList ? [produtoList] : []]
        });

        // Update total when quantity changes
        group.get('quantidade')?.valueChanges.subscribe(qtd => {
            const custo = group.get('custoExibido')?.value || 0;
            group.get('total')?.setValue(qtd * custo);
        });

        // Disable product selection if editing an existing item
        if (this.isEditMode && data?.idProdutoEstruturaProducao) {
            group.get('idProdutoInsumo')?.disable();
        }

        this.producao.push(group);
    }

    removeProducaoRow(index: number): void {
        const item = this.producao.at(index);
        const isExisting = item.get('isExistingItem')?.value;
        const nomeProduto = item.get('nomeProdutoInsumo')?.value;

        if (isExisting) {
            // Show confirmation modal for existing items
            const dialogRef = this.dialog.open(ConfirmModalComponent, {
                data: {
                    title: 'Confirmar Remoção',
                    innerHtml: `
                            <p>Tem certeza que deseja remover o insumo <strong>${nomeProduto}</strong> da estrutura de produção?</p>
                            <p class="text-muted">Esta ação não poderá ser desfeita após salvar.</p>
                        `,
                    buttonConfirm: 'Remover',
                    buttonClose: 'Cancelar'
                }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result?.confirm) {
                    this.producao.removeAt(index);
                }
            });
        } else {
            // Remove directly if it's a new item
            this.producao.removeAt(index);
        }
    }

    atualizarCustoProduto(): void {
        const items = this.producao.getRawValue();
        const custoTotal = items.reduce((acc: number, curr: any) => {
            return acc + (curr.total || 0);
        }, 0);

        this.custoAtualizado.emit(custoTotal);
    }

    addEtapaProducaoRow(data?: any): void {
        const group = this.fb.group({
            id: [data?.idProdutoEtapaProducao || data?.id],
            sequencia: [data?.sequencia],
            descricao: [data?.descricao || '', Validators.required]
        });
        this.etapasProducao.push(group);
    }
    removeEtapaProducaoRow(index: number): void { this.etapasProducao.removeAt(index); }

    dropEtapa(event: CdkDragDrop<AbstractControl[]>): void {
        const array = this.etapasProducao;
        // Cópia independente: array.controls é a mesma referência interna do FormArray, então
        // remover itens de "array" abaixo esvaziaria "controls" junto (mesmo objeto) antes do
        // reinsert rodar — foi isso que fazia a lista sumir. O spread quebra essa referência.
        const controls = [...array.controls];

        // moveItemInArray reordena a cópia; em seguida removemos tudo do FormArray e reinserimos
        // na nova ordem usando os MESMOS FormGroups (não recria), preservando validators/dirty/touched.
        moveItemInArray(controls, event.previousIndex, event.currentIndex);

        while (array.length) {
            array.removeAt(0, { emitEvent: false });
        }
        controls.forEach((ctrl, idx) => array.insert(idx, ctrl, { emitEvent: false }));

        array.updateValueAndValidity();
    }

    private preencherInsumos(insumos: any[]): void {
        this.producao.clear();
        insumos.forEach(i => this.addProducaoRow(i));
    }

    private preencherEtapas(etapas: any[]): void {
        this.etapasProducao.clear();
        etapas.forEach(e => this.addEtapaProducaoRow(e));
    }

    private getCustoExibido(precoCusto: number, custoMedio: number): number {
        if (!this.configuracaoProduto) {
            return precoCusto; // Default para preço de custo
        }

        const tipoCusto = this.configuracaoProduto.idTipoCustoKitFabricado;
        return tipoCusto === TipoCustoKitFabricadoEnum.CustoMedio ? custoMedio : precoCusto;
    }

    onProdutoProducaoSelected(event: any, index: number): void {
        console.log(event)
        if (!event) return;
        const group = this.producao.at(index) as FormGroup;

        // Determinar qual custo usar baseado na configuração
        const custoExibido = this.getCustoExibido(event.precoCusto || event.custo || 0, event.custoMedio || 0);

        group.patchValue({
            idUnidadeConsumo: event.idUnidadeMedida || event.idUnidade || null,
            nomeProdutoInsumo: event.descricao,
            skuProdutoInsumo: event.codigoSku || event.sku,
            unidade: event.unidadeMedida || event.unidade || '',
            precoCusto: event.precoCusto || event.custo || 0,
            custoMedio: event.custoMedio || 0,
            custoExibido: custoExibido
        });

        // Recalculate total
        const qtd = group.get('quantidade')?.value || 0;
        group.get('total')?.setValue(qtd * custoExibido);
    }
}
